import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './VehiculoMantenimientoModal';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';

const PAGE_SIZE = 10;
const nombreTabla = "Mantenimientos de Vehículo";
const endpoint = ENDPOINTS.vehiculoMantenimiento;
const endpointById = ENDPOINTS.vehiculoMantenimientoById;
const modelo = Models.vehiculoMantenimiento;

// 👉 catálogo de vehículos
const endpointVehiculo = ENDPOINTS.vehiculo;
const modeloVehiculo = Models.vehiculo;

const maps = {
  mapCreate: (f) => ({
    idVehiculo: Number(f.idVehiculo) || 0,
    tipo: f.tipo,
    descripcion: f.descripcion,
    fechaProgramada: f.fechaProgramada, // YYYY-MM-DD
    fechaReal: f.fechaReal,             // YYYY-MM-DD (opcional)
    costo: Number(f.costo) || 0,
    km: Number(f.km) || 0,
    activo: !!f.activo,
  }),
  mapUpdate: (id, f) => ({
    idMantenimiento: id,
    idVehiculo: Number(f.idVehiculo) || 0,
    tipo: f.tipo,
    descripcion: f.descripcion,
    fechaProgramada: f.fechaProgramada,
    fechaReal: f.fechaReal,
    costo: Number(f.costo) || 0,
    km: Number(f.km) || 0,
    activo: !!f.activo,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  // 📦 catálogo de vehículos
  const [vehiculos, setVehiculos] = useState([]);
  const [vehiculosError, setVehiculosError] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(endpoint);
      setRows((res.data || []).map((x) => modelo(x)));
    } catch (e) {
      console.error(e);
      alert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  // cargar mantenimientos
  useEffect(() => { loadData(); }, []);

  // cargar catálogo de vehículos una vez
  useEffect(() => {
    let canceled = false;

    async function loadVehiculos() {
      try {
        const res = await axios.get(endpointVehiculo);
        const vehsNorm = (res.data || []).map((x) => modeloVehiculo(x));
        if (!canceled) {
          setVehiculos(vehsNorm);
        }
      } catch (err) {
        console.error('Error cargando catálogo de vehículos', err);
        if (!canceled) {
          setVehiculosError('No se pudieron cargar los vehículos.');
        }
      }
    }

    loadVehiculos();
    return () => {
      canceled = true;
    };
  }, []);

  // diccionario idVehiculo -> vehículo
  const vehiculosById = useMemo(() => {
    const map = new Map();
    vehiculos.forEach(v => {
      if (v.idVehiculo != null) {
        map.set(v.idVehiculo, v);
      }
    });
    return map;
  }, [vehiculos]);

  // helper para mostrar el vehículo (placa + marca/modelo/año)
  function labelVehiculo(row) {
    const v = vehiculosById.get(row.idVehiculo);
    if (!v) {
      const idText = normalizeValue(row.idVehiculo);
      return idText ? `Vehículo #${idText}` : 'Vehículo no asignado';
    }

    const placa = v.placa || 'S/placa';
    const marca = v.marca || '';
    const modeloV = v.modelo || '';
    const anio = v.anio || '';

    const detalle = [marca, modeloV, anio].filter(Boolean).join(' ');
    return detalle ? `${placa} · ${detalle}` : placa;
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const tipo = (r.tipo || '').toLowerCase();
      const desc = (r.descripcion || '').toLowerCase();
      const fp = (r.fechaProgramada || '').toLowerCase();
      const fr = (r.fechaReal || '').toLowerCase();
      const costo = String(r.costo ?? '').toLowerCase();
      const km = String(r.km ?? '').toLowerCase();

      const v = vehiculosById.get(r.idVehiculo);
      const placa = (v?.placa || '').toLowerCase();
      const eco = (v?.economico || '').toLowerCase();
      const marca = (v?.marca || '').toLowerCase();
      const modeloV = (v?.modelo || '').toLowerCase();
      const anio = String(v?.anio ?? '').toLowerCase();

      const vehId = String(r.idVehiculo ?? '').toLowerCase(); // fallback

      return (
        tipo.includes(q) ||
        desc.includes(q) ||
        fp.includes(q) ||
        fr.includes(q) ||
        placa.includes(q) ||
        eco.includes(q) ||
        marca.includes(q) ||
        modeloV.includes(q) ||
        anio.includes(q) ||
        vehId.includes(q) ||
        costo.includes(q) ||
        km.includes(q)
      );
    });
  }, [rows, query, vehiculosById]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idMantenimiento === id);
    if (!r) {
      alert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idMantenimiento ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por vehículo (placa, marca…), tipo, descripción, fechas, costo o km…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Vehículo</th>
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th>Fecha programada</th>
                  <th>Fecha real</th>
                  <th>Costo</th>
                  <th>KM</th>
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idMantenimiento}>
                    <td>{labelVehiculo(r)}</td>
                    <td><strong>{normalizeValue(r.tipo)}</strong></td>
                    <td>{normalizeValue(r.descripcion)}</td>
                    <td>{normalizeValue(r.fechaProgramada)}</td>
                    <td>{normalizeValue(r.fechaReal)}</td>
                    <td>{normalizeValue(r.costo)}</td>
                    <td>{normalizeValue(r.km)}</td>

                    {/* 🔽 Botón EDITAR con ícono */}
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--edit"
                        onClick={() => handleEdit(r.idMantenimiento)}
                        aria-label="Editar"
                        title="Editar"
                      >
                        <img src={sigmaEdit} alt="" className="rk-ico" />
                      </button>
                    </td>

                    {/* 🔽 Botón BORRAR con ícono */}
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--del"
                        onClick={() => {
                          setEditingRowId(null);
                          const name = labelVehiculo(r);
                          askDelete(r.idMantenimiento, name);
                        }}
                        aria-label="Borrar"
                        title="Borrar"
                      >
                        <img src={sigmaDelete} alt="" className="rk-ico" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && paged.length === 0 && (<tr><td colSpan={10}>Sin resultados.</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {vehiculosError && (
          <div style={{ padding: '0.5rem 1rem', color: '#b91c1c', fontSize: '.8rem' }}>
            {vehiculosError}
          </div>
        )}

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}
